<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useSearchStore } from "@/stores/search";
import { searchTerm } from "@/globalConsts/searchBarItems";
import { storeToRefs } from "pinia";
import { t } from "@/services/i18n";

const props = defineProps({
    value: {
        type: String,
        default: "",
    },
    label: {
        type: String,
        default: "",
    },
    placeholder: {
        type: String,
        default: "",
    },
    hasSearchButton: {
        type: Boolean,
        required: true,
    },
    isOpen: {
        type: Boolean,
        default: false,
    },
    inputReadonly: {
        type: Boolean,
        default: true,
    },
    id: {
        type: String,
        required: true,
    },
});

const store = useSearchStore();
const emit = defineEmits(["update:value", "onSubmit", "next"]);
const input = ref<HTMLElement>();
const modelValue = ref("");
const isFilled = computed(() => {
    return store.searchBarIsFilled;
});
const { isPalm } = storeToRefs(store);

function reset() {
    if (props.id === searchTerm) {
        modelValue.value = "";
        updateCachedSearchTerm();
    }
    store.resetSearchBarParameter(props.id);
}

function updateCachedSearchTerm() {
    store.cacheSearchTerm(modelValue.value);
}

onMounted(() => {
    if (props.isOpen && !isPalm.value) {
        input.value?.focus();
    }
    if (props.value) {
        modelValue.value = props.value;
    }
});
</script>
<template>
    <div
        class="[ c-search-bar__item ]"
        :class="{ '[ c-quicksearch__content ]': isOpen }"
    >
        <div
            class="[ c-search-bar__input-box ]"
            :class="{ 'is-filled': isFilled || isOpen }"
        >
            <label class="[ c-search-bar__label ]" :for="id">
                {{ t(label) }}
            </label>
            <input
                ref="input"
                :id="id"
                v-model="modelValue"
                :placeholder="t(placeholder)"
                class="[ c-search-bar__input ]"
                type="text"
                :readonly="inputReadonly"
                tabindex="0"
                @input="updateCachedSearchTerm"
                @keydown.enter="emit('next')"
                @keydown.tab="emit('next')"
            />
        </div>
        <button
            v-if="modelValue && isOpen"
            class="[ c-search-bar__reset ]"
            @click="reset"
        >
            <i class="[ c-icon icon-close c-icon--medium ]"></i>
        </button>
    </div>
</template>
